import React, { useEffect, useMemo, useState } from 'react'
import { graphql, useFragment } from 'react-relay'
import { Trans } from '@lingui/react/macro'

import { XStack, YStack, isWeb } from 'tamagui'

import { lazy as loadable } from '@loadable/component'

import {
  Paragraph,
  Icon,
  Text,
  Card,
  EventAttendButton,
  EventAttendeesOverlap,
  Dialog,
  Link,
} from '@newComponents'

import { EventScreenInviteDialog } from './EventScreenInviteDialog'

import { wrapSuspenseDialog } from '@newComponents/SuspenseWrapper'

import { differenceInCalendarDays } from 'date-fns'

import { EmojiIcon } from '@newComponents/EmojiIcon/EmojiIcon'

import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSequence,
  withTiming,
  withDelay,
  Easing,
  runOnJS,
} from 'react-native-reanimated'

import {
  EventScreenAttendCard_event$key,
  EventScreenAttendCard_event$data,
} from './__generated__/EventScreenAttendCard_event.graphql'
import {
  EventScreenAttendCard_query$key,
  EventScreenAttendCard_query$data,
} from './__generated__/EventScreenAttendCard_query.graphql'

const EventScreenAttendeesDialog = wrapSuspenseDialog(
  loadable(() => import('./EventScreenAttendeesDialog'))
)

const MIN_ATTENDEES_TO_RENDER = 0

export const EventScreenAttendCard = ({
  event,
  query,
}: {
  event: EventScreenAttendCard_event$key
  query: EventScreenAttendCard_query$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventScreenAttendCard_event on Event {
        slug
        prettyUrl
        startAt
        endAt

        eventAttendeesByRelevance(
          filter: {
            status: {
              in: [
                ATTENDING_IN_PERSON
                ATTENDING_VIRTUAL
                WAITING_IN_PERSON
                WAITING_VIRTUAL
              ]
            }
          }
          first: 8
        ) {
          totalCount
          ...EventAttendeesOverlap_eventAttendees
        }

        attendingCount: eventAttendees(
          filter: { status: { in: [ATTENDING_IN_PERSON, ATTENDING_VIRTUAL] } }
        ) {
          totalCount
        }

        waitingCount: eventAttendees(
          filter: { status: { in: [WAITING_IN_PERSON, WAITING_VIRTUAL] } }
        ) {
          totalCount
        }

        viewerCanManage

        myAttendance {
          visibleStatus
          paymentStatus
          formSubmission {
            id
          }
        }

        myTicketOrder {
          status
        }

        attendeeForm {
          rowId
        }

        eventTicketingTiers(orderBy: [SORT_ORDER_ASC, CREATED_AT_ASC]) {
          totalCount
        }

        ...EventAttendButtonNew_event
        ...EventScreenInviteDialog_event
      }
    `,
    event
  )

  const queryData = useFragment(
    graphql`
      fragment EventScreenAttendCard_query on Query {
        ...EventAttendButtonNew_query

        viewer {
          authorizationLevel
        }
      }
    `,
    query
  )

  return (
    <Card glass gap='$4' borderRadius='$8' padding='$4' paddingBottom={14}>
      <EventScreenAttendCardHeader data={data} />

      <XStack
        gap='$4'
        paddingTop='$1'
        alignItems='center'
        // display='none'
        // $gtLg={{ display: 'flex' }}
      >
        <EventScreenAttendeesDialogTrigger data={data} queryData={queryData} />

        {new Date().getTime() <= new Date(data.endAt).getTime() &&
        // Show invite if user is attending/waiting
        ((data.myAttendance &&
          !['NOT_ATTENDING', 'PENDING_APPROVAL'].includes(
            data.myAttendance.visibleStatus
          )) ||
          // Or if user is an organizer/editor/owner/admin
          queryData.viewer?.authorizationLevel === 'ADMIN' ||
          data.viewerCanManage) ? (
          <EventScreenInviteDialog event={data} />
        ) : null}

        <EventAttendButton
          event={data}
          query={queryData}
          height={32}
          minHeight={32}
          borderRadius={16}
          animateChevron
        />
      </XStack>
    </Card>
  )
}

const WavingEmojiIcon = () => {
  const rotation = useSharedValue(0)

  useEffect(() => {
    const ease = Easing.inOut(Easing.ease)
    rotation.value = withDelay(
      300,
      withSequence(
        withTiming(12, { duration: 200, easing: ease }),
        withTiming(-10, { duration: 200, easing: ease }),
        withTiming(12, { duration: 200, easing: ease }),
        withTiming(-6, { duration: 200, easing: ease }),
        withTiming(8, { duration: 200, easing: ease }),
        withTiming(0, { duration: 300, easing: ease })
      )
    )
  }, [])

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }))

  return (
    <Animated.View
      style={[{ transformOrigin: '70% 80%' } as any, animatedStyle]}
    >
      <EmojiIcon size='$5' name='waving-hand' />
    </Animated.View>
  )
}

// Nudges attendees who secured their spot with a ticket purchase (or an
// approval-gated hold) but haven't filled out the event's registration form
// yet (forms come after checkout on ticketed events). The attend screen shows
// the form for paid and reserved attendees.
const CompleteRegistrationPrompt = ({
  data,
}: {
  data: EventScreenAttendCard_event$data
}) => {
  const hasPaidOrHeldTicket =
    data.myAttendance?.paymentStatus === 'SUCCEEDED' ||
    data.myTicketOrder?.status === 'REQUIRES_CAPTURE'

  if (
    !data.attendeeForm ||
    data.eventTicketingTiers.totalCount === 0 ||
    !hasPaidOrHeldTicket ||
    data.myAttendance?.formSubmission
  ) {
    return null
  }

  return (
    <XStack gap='$1' alignItems='center'>
      <Paragraph size='$4'>
        <Trans>
          One more step:{' '}
          <Link
            size='$4'
            color='$primaryColor'
            href={`/events/${data.prettyUrl}/attend`}
          >
            complete your registration form
          </Link>
        </Trans>
      </Paragraph>
    </XStack>
  )
}

const EventScreenAttendCardHeader = ({
  data,
}: {
  data: EventScreenAttendCard_event$data
}) => {
  const daysToGo = useMemo(() => {
    return differenceInCalendarDays(new Date(data.startAt), new Date())
  }, [data.startAt])

  const eventInPast = useMemo(() => {
    return new Date().getTime() > new Date(data.endAt).getTime()
  }, [data.endAt])

  if (
    !data.myAttendance ||
    data.myAttendance.visibleStatus === 'NOT_ATTENDING'
  ) {
    if (eventInPast) {
      return (
        <Paragraph size='$4' paddingTop='$1'>
          <Trans>We missed you this time around!</Trans>
        </Paragraph>
      )
    }

    return (
      <>
        <XStack gap='$2' alignItems='center' paddingTop='$1'>
          <WavingEmojiIcon />
          <Paragraph size='$4'>
            <Trans>Ready to join in on the fun?</Trans>
          </Paragraph>
        </XStack>

        {data.eventTicketingTiers.totalCount > 0 &&
          data.myAttendance?.paymentStatus !== 'SUCCEEDED' && (
            <Paragraph size='$4'>
              <Trans>Grab a ticket to secure your spot</Trans>
            </Paragraph>
          )}

        {data.myAttendance?.visibleStatus === 'NOT_ATTENDING' && (
          <Paragraph size='$4'>
            <Trans>
              You&apos;re not attending, and won&apos;t receive notifications
              for this event.
            </Trans>
          </Paragraph>
        )}
      </>
    )
  }

  if (
    ['WAITING_IN_PERSON', 'WAITING_VIRTUAL'].includes(
      data.myAttendance.visibleStatus
    )
  ) {
    if (eventInPast) {
      return (
        <Paragraph size='$4' paddingTop='$1'>
          <Trans>We missed you this time around!</Trans>
        </Paragraph>
      )
    }

    if (
      data.eventTicketingTiers.totalCount &&
      data.myAttendance.paymentStatus !== 'SUCCEEDED'
    ) {
      return (
        <>
          <XStack gap='$2' alignItems='center' paddingTop='$1'>
            <EmojiIcon size='$5' name='waving-hand' />
            <Paragraph size='$4'>
              <Trans>Ready to join in on the fun?</Trans>
            </Paragraph>
          </XStack>

          <Paragraph size='$4'>
            <Trans>Grab a ticket to secure your spot</Trans>
          </Paragraph>
        </>
      )
    }

    return (
      <>
        <Paragraph size='$4' paddingTop='$1'>
          <Trans>You&apos;re waiting for a spot to become available.</Trans>
        </Paragraph>

        <Paragraph size='$4'>
          <Trans>We&apos;ll email you if you get one!</Trans>
        </Paragraph>

        <CompleteRegistrationPrompt data={data} />
      </>
    )
  }

  if (data.myAttendance.visibleStatus === 'PENDING_APPROVAL') {
    return (
      <>
        <XStack gap='$2' alignItems='center' paddingTop='$1'>
          <Icon name='clock' size='$5' color='$colorMuted' />
          <Paragraph size='$4'>
            <Trans>Your registration is pending approval.</Trans>
          </Paragraph>
        </XStack>

        <Paragraph size='$4'>
          <Trans>You will be notified when it has been reviewed.</Trans>
        </Paragraph>

        {data.myTicketOrder?.status === 'REQUIRES_CAPTURE' && (
          <Paragraph size='$4'>
            <Trans>
              Your payment is on hold and will only be charged if you&apos;re
              approved.
            </Trans>
          </Paragraph>
        )}
      </>
    )
  }

  if (daysToGo > 0) {
    return (
      <>
        <XStack gap='$1' alignItems='center' paddingTop='$1'>
          <Paragraph size='$4'>
            <Trans>You&apos;re attending!</Trans>
          </Paragraph>

          <EmojiIcon size='$4' name='party-popper' />
        </XStack>

        <XStack gap='$1' alignItems='center'>
          <Paragraph size='$4'>
            <Trans>
              Only {daysToGo} more {daysToGo === 1 ? 'day' : 'days'} to go
            </Trans>
          </Paragraph>

          <EmojiIcon size='$4' name='raising-hands' />
        </XStack>

        <CompleteRegistrationPrompt data={data} />
      </>
    )
  }

  if (daysToGo === 0) {
    return (
      <>
        <XStack gap='$1' alignItems='center' paddingTop='$1'>
          <Paragraph size='$4'>
            <Trans>You&apos;re attending!</Trans>
          </Paragraph>

          <EmojiIcon size='$4' name='party-popper' />
        </XStack>

        <XStack gap='$1' alignItems='center'>
          <Paragraph size='$4'>
            <Trans>Today is the day</Trans>
          </Paragraph>

          <EmojiIcon size='$4' name='raising-hands' />
        </XStack>

        <CompleteRegistrationPrompt data={data} />
      </>
    )
  }

  return (
    <>
      <XStack gap='$1' alignItems='center' paddingTop='$1'>
        <Paragraph size='$4'>
          <Trans>You attended!</Trans>
        </Paragraph>

        <EmojiIcon size='$4' name='party-popper' />
      </XStack>

      <XStack gap='$1' alignItems='center'>
        <Paragraph size='$4'>
          <Trans>We look forward to seeing you next time</Trans>
        </Paragraph>

        <EmojiIcon size='$4' name='waving-hand' />
      </XStack>
    </>
  )
}

const EventScreenAttendeesDialogTrigger = ({
  data,
  queryData,
}: {
  data: EventScreenAttendCard_event$data
  queryData: EventScreenAttendCard_query$data
}) => {
  const [attendeesWidth, setAttendeesWidth] = useState(200)

  const limitAttendees = useMemo(() => {
    const singleAttendeeWidth = 28

    const computedWidth = (attendeesWidth - singleAttendeeWidth) * (3 / 2)

    return Math.max(Math.floor(computedWidth / singleAttendeeWidth), 1)
  }, [attendeesWidth])

  if (
    queryData.viewer?.authorizationLevel === 'ADMIN' ||
    data.viewerCanManage ||
    (data.eventTicketingTiers.totalCount === 0 &&
      data.eventAttendeesByRelevance.totalCount > MIN_ATTENDEES_TO_RENDER)
  ) {
    return (
      <Dialog modal>
        <Dialog.Trigger tag='dialog' flex={1}>
          <XStack
            onLayout={(event) => {
              const nextWidth = event.nativeEvent.layout.width
              setAttendeesWidth((previousWidth) =>
                previousWidth === nextWidth ? previousWidth : nextWidth
              )
            }}
            flex={1}
            gap='$4'
            alignItems='center'
            cursor='pointer'
            // onHoverIn={() => {
            //   // TODO: Swap attendee text with hover instructions
            // }}
            // onHoverOut={() => {
            //   // TODO: Swap attendee text with hover instructions
            // }}
          >
            {!!data.eventAttendeesByRelevance.totalCount && (
              <EventAttendeesOverlap
                size='$7'
                attendingCount={data.attendingCount.totalCount}
                waitingCount={data.waitingCount.totalCount}
                eventAttendees={data.eventAttendeesByRelevance}
                limitAttendees={limitAttendees}
              />
            )}
          </XStack>
        </Dialog.Trigger>

        <Dialog.Adapt when='xs'>
          <Dialog.Sheet key='sheet'>
            <Dialog.Sheet.Handle zIndex={200_000} />
            <Dialog.Sheet.Frame
              padding={0}
              gap='$4'
              shadowColor='$shadowColor'
              shadowRadius='$2'
              borderTopRightRadius='$6'
              borderTopLeftRadius='$6'
            >
              <Dialog.Sheet.ScrollView
                // borderTopRightRadius='$6'
                // borderTopLeftRadius='$6'
                paddingTop='$6'
                paddingHorizontal='$6'
              >
                <YStack gap='$4'>
                  {isWeb ? (
                    <EventScreenAttendeesDialog slug={data.slug} />
                  ) : (
                    <Dialog.Adapt.Contents />
                  )}
                </YStack>
              </Dialog.Sheet.ScrollView>
            </Dialog.Sheet.Frame>
          </Dialog.Sheet>
        </Dialog.Adapt>

        <Dialog.Portal>
          <Dialog.Content
            elevate
            key='content'
            gap='$4'
            minWidth={400}
            maxWidth={600}
            minHeight={400}
            maxHeight={600}
            forceMount
          >
            <EventScreenAttendeesDialog slug={data.slug} />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    )
  }

  return <XStack flex={1} gap='$4' alignItems='center' />
}
